import type { ComponentProps, ReactNode } from "react";
import { toast as sonner, Toaster as SonnerToaster } from "sonner";
import { cn } from "@rekati/utils";
import { HaloRim, type RimTone } from "./rim";

export type ToastTone = "info" | "success" | "danger";

const RIM: Record<ToastTone, RimTone> = { info: "paint", success: "success", danger: "danger" };
const BADGE: Record<ToastTone, string> = {
  info: "bg-volt text-volt-foreground",
  success: "bg-paint-teal text-white",
  danger: "bg-destructive text-white",
};

function ToneMark({ tone }: { tone: ToastTone }) {
  const d =
    tone === "success"
      ? "M3.5 8.5 6.5 11.5 12.5 4.5"
      : tone === "danger"
        ? "M8 3.5v5.5M8 12v.5"
        : "M8 7v5M8 4v.5";
  return (
    <span
      className={cn("grid size-7 shrink-0 place-items-center rounded-full", BADGE[tone])}
      aria-hidden="true"
    >
      <svg viewBox="0 0 16 16" className="size-3.5">
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth={2.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/**
 * Cult UI's Halo Toast, restyled flat: a white card with a hairline edge and
 * soft depth, the paint drifting faintly along its foot as its one accent.
 * Sonner slides it in; its live region announces it, and `danger` interrupts.
 */
function ToastSurface({
  id,
  title,
  description,
  tone,
  action,
  closeLabel,
}: {
  id: string | number;
  title: string;
  description?: string | undefined;
  tone: ToastTone;
  action?: ReactNode;
  closeLabel: string;
}) {
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className="relative isolate w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-soft"
    >
      <HaloRim tone={RIM[tone]} strength={0.3} className="-z-10" />
      <div className="flex items-start gap-3 p-4">
        <ToneMark tone={tone} />
        <div className="grid min-w-0 flex-1 gap-1">
          <p className="font-display text-[0.95rem] leading-snug font-semibold">{title}</p>
          {description ? (
            <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
          ) : null}
          {action ? <div className="mt-1.5">{action}</div> : null}
        </div>
        <button
          type="button"
          onClick={() => sonner.dismiss(id)}
          aria-label={closeLabel}
          className="-mt-1 -mr-1 grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <svg viewBox="0 0 16 16" className="size-4" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export interface NotifyOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  action?: ReactNode;
  /** Milliseconds; danger stays longer by default. */
  duration?: number;
  /** Replaces a toast with the same id instead of stacking another. */
  id?: string;
  closeLabel?: string;
}

/** Shows one toast. Transient news only; an error that belongs to a field stays under the field. */
export function notify({
  title,
  description,
  tone = "info",
  action,
  duration,
  id,
  closeLabel = "Dismiss",
}: NotifyOptions) {
  return sonner.custom(
    (tid) => (
      <ToastSurface
        id={tid}
        title={title}
        description={description}
        tone={tone}
        action={action}
        closeLabel={closeLabel}
      />
    ),
    { duration: duration ?? (tone === "danger" ? 7000 : 4500), ...(id ? { id } : {}), unstyled: true },
  );
}

/** Mount once per app. Bottom centre on a phone, bottom right on a wide screen. */
export function Toaster(props: ComponentProps<typeof SonnerToaster>) {
  return (
    <SonnerToaster
      position="bottom-right"
      gap={12}
      offset={20}
      mobileOffset={12}
      visibleToasts={3}
      {...props}
    />
  );
}
